import {assert} from 'Asserts';
import * as AuditActions from 'audit/AuditActions';
import {EXPORT_AUDIT_LOG_DISPLAY_STRING} from 'audit/AuditConstants';
import type {
  StoichiometryCreateGroupExtras,
  StoichiometryGroupMetadata,
} from 'notebook/integrations/chemistry/StoichiometryNoteLinkedGroupAdapter';
import makeSimpleGroupActionFunction from 'notebook/notes/groups/action-functions/makeSimpleGroupActionFunction';
import {findNotePartForStructuredTable} from 'notebook/notes/groups/util/ContainedNotePartUtil';
import toColumnLabel from 'notebook/structured-tables/columns/toColumnLabel';
import {getStructuredTableAdapters} from 'notebook/structured-tables/StructuredTableAdapters';
import {
  StructuredTableKind,
  UnknownStructuredTable,
} from 'notebook/structured-tables/StructuredTableConstants';
import {EvaluatedCell} from 'notebook/tables/excess/EvaluationConstants';
import makeTableCSV from 'notebook/tables/export/makeTableCSV';
import {isStoichiometryGroup} from 'notebook/TypePredicates';
import {initiateTextDownload} from 'util/TextToBlobUtil';

async function initiateCSVDownload(
  structuredTable: UnknownStructuredTable,
  computedTable: Array<Array<EvaluatedCell>>
): Promise<void> {
  const structuredTableAdapter = getStructuredTableAdapters()[structuredTable.kind];
  const tableCells = structuredTableAdapter.toTable(structuredTable).table;

  initiateTextDownload(
    await makeTableCSV({
      table: tableCells,
      computedTable,
      headerRow: structuredTable.columns.map(
        (structuredTableColumn) =>
          toColumnLabel({structuredTableColumn, schemas: structuredTable.schemas}) || 'unknown'
      ),
      includeCellValueModifiers: false,
    }),
    `${structuredTable.name}.csv`
  );
}

export const downloadStoichiometryCSVActionFunction = makeSimpleGroupActionFunction<
  StoichiometryGroupMetadata,
  StoichiometryCreateGroupExtras
>({
  label: 'Download as CSV',
  icon: 'download',

  onInvoke: ({noteLinkedGroup, evaluatedTableMap}) => {
    assert(
      isStoichiometryGroup(noteLinkedGroup),
      'Stoichiometry CSV action can only be used with stoichiometry groups'
    );
    const notePart = findNotePartForStructuredTable(noteLinkedGroup, StructuredTableKind.STOICHIOMETRY);
    assert(notePart != null, 'Stoichiometry group must have a structured table note part');
    const structuredTable = notePart.item as UnknownStructuredTable;
    const computedTable = evaluatedTableMap[structuredTable.name];
    assert(computedTable != null, 'Unable to find evaluated table');
    initiateCSVDownload(structuredTable, computedTable);
  },
});

export const exportStoichiometryAuditLogsActionFunction = makeSimpleGroupActionFunction<
  StoichiometryGroupMetadata,
  StoichiometryCreateGroupExtras
>({
  label: EXPORT_AUDIT_LOG_DISPLAY_STRING,
  icon: 'audit-trail',

  onInvoke: ({noteLinkedGroup}) => {
    assert(
      isStoichiometryGroup(noteLinkedGroup),
      'Stoichiometry audit log action can only be used with stoichiometry groups'
    );
    const notePart = findNotePartForStructuredTable(noteLinkedGroup, StructuredTableKind.STOICHIOMETRY);
    assert(notePart != null, 'Stoichiometry group must have a structured table note part');
    const structuredTable = notePart.item as UnknownStructuredTable;
    assert(structuredTable.apiId != null, 'Stoichiometry table must have an apiId to export audit logs');
    AuditActions.exportAuditLogs([structuredTable.apiId]);
  },

  getDisabled: ({noteLinkedGroup}) => {
    const notePart = findNotePartForStructuredTable(noteLinkedGroup, StructuredTableKind.STOICHIOMETRY);
    if (notePart == null) {
      return true;
    }
    // Disable when the table hasn't been saved yet (apiId is null on a freshly-inserted table)
    return (notePart.item as UnknownStructuredTable).apiId == null;
  },
});
